import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { formatDistanceToNow } from 'date-fns';

const BatchProcessingPanel = ({ 
  jobs = [], 
  onStartBatchProcess, 
  isProcessing = false, 
  processingProgress = 0 
}) => {
  const [batchConfig, setBatchConfig] = useState({
    processType: 'lead_discovery',
    batchSize: 500,
    parallelJobs: 3,
    retryFailedItems: true,
    enableRealTimeUpdates: true,
    notifyOnCompletion: true,
    priority: 'normal'
  });

  const [selectedJobs, setSelectedJobs] = useState([]);

  const handleConfigChange = (field, value) => {
    setBatchConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleJobSelect = (jobId) => {
    const isSelected = selectedJobs?.includes(jobId);
    if (isSelected) {
      setSelectedJobs(selectedJobs?.filter(id => id !== jobId));
    } else {
      setSelectedJobs([...selectedJobs, jobId]);
    }
  };

  const handleSelectAll = () => {
    const allJobIds = jobs?.map(job => job?.id) || [];
    if (selectedJobs?.length === allJobIds?.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(allJobIds);
    }
  };

  const handleStartBatch = () => {
    onStartBatchProcess?.(batchConfig);
  };

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50 border-green-200';
      case 'running': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'failed': return 'text-red-600 bg-red-50 border-red-200';
      case 'cancelled': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getJobStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'running': return 'Loader2';
      case 'pending': return 'Clock';
      case 'failed': return 'XCircle';
      case 'cancelled': return 'StopCircle';
      default: return 'HelpCircle';
    }
  };

  const processTypes = [
    { value: 'lead_discovery', label: 'Lead Discovery' },
    { value: 'validation', label: 'Contact Validation' },
    { value: 'data_cleansing', label: 'Data Cleansing' },
    { value: 'enrichment', label: 'Data Enrichment' },
    { value: 'duplicate_detection', label: 'Duplicate Detection' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'normal', label: 'Normal Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const runningJobs = jobs?.filter(job => job?.status === 'running') || [];
  const completedJobs = jobs?.filter(job => job?.status === 'completed') || [];
  const failedJobs = jobs?.filter(job => job?.status === 'failed') || [];

  return (
    <div className="space-y-6">
      {/* Batch Processing Overview */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4">
          Bulk Lead Processing
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{runningJobs?.length}</div>
            <div className="text-sm text-muted-foreground">Active Jobs</div>
          </div>
          <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{completedJobs?.length}</div>
            <div className="text-sm text-green-600">Completed</div>
          </div>
          <div className="text-center p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{failedJobs?.length}</div>
            <div className="text-sm text-red-600">Failed</div>
          </div>
          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-2xl font-bold text-foreground">{jobs?.length}</div>
            <div className="text-sm text-muted-foreground">Total Jobs</div>
          </div>
        </div>
      </div>

      {/* Batch Configuration */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          New Batch Processing Job
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Select
            label="Process Type"
            value={batchConfig?.processType}
            onChange={(value) => handleConfigChange('processType', value)}
            options={processTypes}
          />
          
          <Select
            label="Priority"
            value={batchConfig?.priority}
            onChange={(value) => handleConfigChange('priority', value)}
            options={priorityOptions}
          />
          
          <Input
            label="Batch Size"
            type="number"
            value={batchConfig?.batchSize}
            onChange={(e) => handleConfigChange('batchSize', parseInt(e?.target?.value))}
            min="100"
            max="2000"
            step="100"
          />
          
          <Input
            label="Parallel Jobs"
            type="number"
            value={batchConfig?.parallelJobs}
            onChange={(e) => handleConfigChange('parallelJobs', parseInt(e?.target?.value))}
            min="1"
            max="10"
          />
        </div>

        <div className="space-y-3 mb-6">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="retryFailedItems"
              checked={batchConfig?.retryFailedItems}
              onChange={(checked) => handleConfigChange('retryFailedItems', checked)}
            />
            <label htmlFor="retryFailedItems" className="text-sm text-foreground cursor-pointer">
              Automatically retry failed items
            </label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="enableRealTimeUpdates"
              checked={batchConfig?.enableRealTimeUpdates}
              onChange={(checked) => handleConfigChange('enableRealTimeUpdates', checked)}
            />
            <label htmlFor="enableRealTimeUpdates" className="text-sm text-foreground cursor-pointer">
              Enable real-time progress updates
            </label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="notifyOnCompletion"
              checked={batchConfig?.notifyOnCompletion}
              onChange={(checked) => handleConfigChange('notifyOnCompletion', checked)}
            />
            <label htmlFor="notifyOnCompletion" className="text-sm text-foreground cursor-pointer">
              Send notification when completed
            </label>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Estimated time: ~{Math.ceil(batchConfig?.batchSize / 250)} minutes
          </div>
          <Button
            onClick={handleStartBatch}
            disabled={isProcessing}
            variant="primary"
          >
            {isProcessing ? (
              <>
                <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Icon name="Play" size={16} className="mr-2" />
                Start Batch Process
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Current Processing Job */}
      {isProcessing && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Current Processing Job</h3>
            <span className="text-sm text-muted-foreground">{processingProgress}% complete</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div 
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${processingProgress}%` }}
            />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Type: </span>
              <span className="text-foreground font-medium capitalize">
                {batchConfig?.processType?.replace('_', ' ')}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Batch Size: </span>
              <span className="text-foreground font-medium">{batchConfig?.batchSize}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Parallel Jobs: </span>
              <span className="text-foreground font-medium">{batchConfig?.parallelJobs}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Priority: </span>
              <span className="text-foreground font-medium capitalize">{batchConfig?.priority}</span>
            </div>
          </div>
        </div>
      )}

      {/* Job History */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            Processing Jobs ({jobs?.length})
          </h3>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedJobs?.length === jobs?.length ? 'Deselect All' : 'Select All'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => console.log('Refresh jobs')}
            >
              <Icon name="RefreshCw" size={14} className="mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          {jobs?.map((job) => (
            <div 
              key={job?.id} 
              className={`p-4 border rounded-lg transition-all ${
                selectedJobs?.includes(job?.id) 
                  ? 'border-primary bg-primary/5' :'border-border hover:border-muted-foreground'
              }`}
            >
              <div className="flex items-start space-x-3">
                <Checkbox
                  checked={selectedJobs?.includes(job?.id)}
                  onChange={() => handleJobSelect(job?.id)}
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-foreground">{job?.name}</h4>
                      <p className="text-sm text-muted-foreground capitalize">
                        {job?.type?.replace('_', ' ')} • {job?.source}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                        getJobStatusColor(job?.status)
                      }`}>
                        <Icon 
                          name={getJobStatusIcon(job?.status)} 
                          size={12} 
                          className={`mr-1 ${job?.status === 'running' ? 'animate-spin' : ''}`}
                        />
                        {job?.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Processed: </span>
                      <span className="text-foreground font-medium">
                        {job?.totalProcessed?.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Success: </span>
                      <span className="text-green-600 font-medium">
                        {job?.successCount?.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Errors: </span>
                      <span className="text-red-600 font-medium">
                        {job?.errorCount?.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Duplicates: </span>
                      <span className="text-yellow-600 font-medium">
                        {job?.duplicateCount?.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {job?.status === 'running' && job?.progress && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>Progress</span>
                        <span>{job?.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${job?.progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                    <div>
                      Started: {formatDistanceToNow(new Date(job?.startedAt), { addSuffix: true })}
                    </div>
                    {job?.completedAt && (
                      <div>
                        Completed: {formatDistanceToNow(new Date(job?.completedAt), { addSuffix: true })}
                      </div>
                    )}
                  </div>

                  {job?.criteria && (
                    <div className="mt-2 p-2 bg-muted rounded text-xs">
                      <strong>Criteria:</strong> {Object?.entries(job?.criteria)?.map(([key, value]) => 
                        `${key}: ${value}`
                      )?.join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {jobs?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Layers" size={48} className="mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Processing Jobs</h3>
              <p>Start your first batch processing job to see it here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchProcessingPanel;